import { SpotterLayer, SpotterLayerProps } from '@sofarocean/atlas/maps';
import { SpotterMapData } from '@sofarocean/atlas/types';
import MapboxContext from '@sofarocean/mapbox-context/dist/contexts/MapboxContext';
import { LngLatLike } from 'mapbox-gl';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import scoutImageSelected from '@/assets/map/scout-selected.png';
import scoutImage from '@/assets/map/scout.png';
import spotterImageYellowSm from '@/assets/map/spotter-yellow-flat-sm.png';
import spotterImageSelectedSm from '@/assets/map/spotter-yellow-selected-sm.png';
import { LocationData } from '@/common/types';
import { SPOTTER_ICONS } from '@/components/map/SpotterMap';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useTetherDeviceIdentity } from '@/hooks/useTetherDeviceIdentity';
import { getSpotterLayerIconName, spotterToLngLat, thinByTimeBucket } from '@/utils';

import { TrackLineLayer } from './TrackLineLayer';

const MAX_SPOTTERS_TO_RENDER = 1000;

export type HistoricalSpotterTrackLayerProps = {
    track: LocationData[];
    selectedTrackItem?: LocationData;
};

export type SpotterTrackLayerProps = {
    track: LocationData[];
    selectedLocation?: LocationData | null;
    isScout?: boolean;
    onSpotterClick?: SpotterLayerProps['onSpotterClick'];
};

export const HistoricalSpotterTrackLayer = ({ track, selectedTrackItem }: HistoricalSpotterTrackLayerProps) => {
    const { setSelectedTimestamp } = useTimelineNavigationContext();
    const [lastCenteredTimestamp, setLastCenteredTimestamp] = useState<string>();
    const { map } = useContext(MapboxContext);
    const allTimestamps = useMemo(() => track?.map(({ timestamp }) => timestamp), [track]);
    const { isScout } = useTetherDeviceIdentity();

    const centerMapIfOutOfBounds = useCallback(
        (center: LngLatLike) => {
            if (!map) {
                return;
            }
            const isCoordInView = map.getBounds()?.contains(center);
            if (!isCoordInView) {
                map.flyTo({
                    center,
                    duration: 500,
                });
            }
        },
        [map]
    );

    // We want keyboard to navigate timeline on this map, not pan
    useEffect(() => map?.keyboard.disable(), [map]);

    useEffect(() => {
        if (!selectedTrackItem || lastCenteredTimestamp === selectedTrackItem.timestamp) {
            return;
        }
        const selectedSpotterPosition = spotterToLngLat(selectedTrackItem);
        centerMapIfOutOfBounds(selectedSpotterPosition);
        setLastCenteredTimestamp(selectedTrackItem.timestamp);
    }, [centerMapIfOutOfBounds, selectedTrackItem, lastCenteredTimestamp]);

    return (
        <SpotterTrackLayer
            track={track}
            selectedLocation={selectedTrackItem}
            isScout={isScout}
            onSpotterClick={(_spotterId, spotter) => {
                setSelectedTimestamp(spotter.timestamp, allTimestamps);
            }}
        />
    );
};

export const SpotterTrackLayer = ({
    track,
    selectedLocation,
    onSpotterClick,
    isScout = false,
}: SpotterTrackLayerProps) => {
    /*
     * Performance gets terrible past a few thousand Spotter icons, so the track is thinned down to
     * MAX_SPOTTERS_TO_RENDER of them. The thinning is by time bucket, which is what keeps the icons already
     * on the map where they are as the track picks up new positions at its newest end.
     */
    const thinnedTrack = useMemo(() => (track ? thinByTimeBucket(track, MAX_SPOTTERS_TO_RENDER) : track), [track]);

    const spotterMapData = useMemo(() => {
        const positions: SpotterMapData[] = [];
        thinnedTrack?.forEach((locationRecord) => {
            const { timestamp, latitude, longitude } = locationRecord || {};
            if (!latitude || !longitude || timestamp === selectedLocation?.timestamp) {
                // we'll add the selected location later to keep it on top
                return;
            }
            const id = `${timestamp}-${latitude}-${longitude}`;
            positions.push({
                id,
                label: timestamp,
                position: {
                    lat: latitude,
                    lon: longitude,
                },
                timestamp,
                icon: getSpotterLayerIconName('default', isScout),
            });
        });
        if (selectedLocation) {
            // put the selected position last so it shows up on top (regardless of whether it's in the thinned locations)
            const { timestamp, latitude, longitude } = selectedLocation;
            const id = `${timestamp}-${latitude}-${longitude}`;
            positions.push({
                id,
                label: timestamp,
                position: {
                    lat: latitude,
                    lon: longitude,
                },
                timestamp,
                icon: getSpotterLayerIconName('selected', isScout),
            });
        }
        return positions;
    }, [thinnedTrack, selectedLocation, isScout]);

    return (
        <>
            <TrackLineLayer trackPositions={track /* Note TrackLineLayer seems to do fine with un-thinned track */} />
            <SpotterLayer
                spotterColor={SPOTTER_ICONS.mine.color}
                onSpotterClick={onSpotterClick}
                spotterIcons={[
                    { name: 'spotter', url: spotterImageYellowSm },
                    { name: 'selected', url: spotterImageSelectedSm },
                    { name: 'scout', url: scoutImage },
                    { name: 'scout-selected', url: scoutImageSelected },
                ]}
                spotterMapData={spotterMapData}
            />
        </>
    );
};
